/**
 * CSV lesen, wie es aus einem echten Warenwirtschaftssystem herausfaellt -- nicht wie im Lehrbuch.
 *
 * Gemessen an den echten C.O.S.-Arista-Exporten (Kundenadresse.CSV 1192 Zeilen, Maschinendaten.CSV
 * 2232 Zeilen, 2026-07-17):
 * - **Windows-1252, kein BOM.** Wer als UTF-8 liest, macht aus "Römerstrasse" ein "R?merstrasse".
 *   Das ist kein Schoenheitsfehler: der Name steht danach so auf der Rechnung.
 * - **CRLF** als Zeilenende.
 * - **Semikolon** als Trenner.
 * - **Zwei Zitierstile aus derselben Quelle**: dieselbe Abfrage lieferte einmal alle Felder in
 *   Anfuehrungszeichen, einmal keines.
 * - **Leere Zeilen mit Trennern**: wer in Excel Zeilen "loescht", hinterlaesst oft `;;;;;;` --
 *   in der Testdatei 1136 von 1192 Zeilen. Ein Import, der die anlegt, legt 1136 leere Kunden an.
 */

export type CsvEncoding = 'utf-8' | 'windows-1252';

export interface DecodedCsv {
  readonly text: string;
  readonly encoding: CsvEncoding;
  /** Woran wir es erkannt haben -- gehoert in die Oberflaeche, damit niemand raten muss. */
  readonly reason: string;
}

const utf8Bom = [0xef, 0xbb, 0xbf];

function hasUtf8Bom(bytes: Uint8Array): boolean {
  return utf8Bom.every((byte, index) => bytes[index] === byte);
}

/**
 * Erkennt die Kodierung, statt sie zu behaupten.
 *
 * Der Trick ist der strenge UTF-8-Dekoder: Windows-1252-Umlaute (ö = 0xF6) sind allein KEIN
 * gueltiges UTF-8 und werfen. Reiner ASCII-Text ist in beiden Kodierungen identisch -- da ist die
 * Wahl egal, nicht falsch.
 *
 * Umgekehrt kann es nicht schiefgehen: eine echte UTF-8-Datei als 1252 zu lesen ergaebe "RÃ¶mer",
 * und genau deshalb wird UTF-8 zuerst geprueft.
 */
export function decodeCsv(bytes: Uint8Array, forced?: CsvEncoding): DecodedCsv {
  if (forced) {
    return {
      text: strip(new TextDecoder(forced).decode(bytes)),
      encoding: forced,
      reason: 'Von Hand ausgewählt.',
    };
  }

  if (hasUtf8Bom(bytes)) {
    return {
      text: strip(new TextDecoder('utf-8').decode(bytes)),
      encoding: 'utf-8',
      reason: 'UTF-8, an der Bytefolge am Dateianfang erkannt (BOM).',
    };
  }

  try {
    const text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    return { text: strip(text), encoding: 'utf-8', reason: 'Gültiges UTF-8.' };
  } catch {
    return {
      text: strip(new TextDecoder('windows-1252').decode(bytes)),
      encoding: 'windows-1252',
      reason: 'Kein gültiges UTF-8 – als Windows-1252 gelesen (so exportiert Arista).',
    };
  }
}

/** BOM entfernen (U+FEFF am Dateianfang): sonst heisst die erste Spalte BOM+"SUCHNAME" und nichts passt. */
function strip(text: string): string {
  return text.startsWith('\uFEFF') ? text.slice(1) : text;
}

export interface CsvRow {
  /**
   * Zeilennummer wie in Excel: 1 = Kopfzeile, 2 = erster Datensatz. Bewusst die ECHTE Nummer und
   * kein laufender Index -- wer einen Fehler sucht, oeffnet die Datei und springt dorthin.
   */
  readonly line: number;
  readonly cells: readonly string[];
}

export interface CsvTable {
  readonly header: readonly string[];
  readonly rows: readonly CsvRow[];
  readonly delimiter: string;
  /** Zeilen, die nur aus Trennern bestanden. Wird gezaehlt, nicht verschwiegen. */
  readonly blankRowsSkipped: number;
  /** Zeilen mit abweichender Spaltenzahl -- fast immer ein unmaskiertes Trennzeichen im Text. */
  readonly raggedLines: readonly number[];
}

const delimiterCandidates = [';', ',', '\t', '|'];

/**
 * Trennzeichen an der Kopfzeile erkennen -- und zwar AUSSERHALB von Anfuehrungszeichen.
 * `"Meier, Hans";"Werl"` hat ein Komma im Text; wer stumpf zaehlt, waehlt das Komma und zerlegt
 * jeden Firmennamen.
 */
export function detectDelimiter(firstLine: string): string {
  let best = ';';
  let bestCount = 0;
  for (const candidate of delimiterCandidates) {
    const count = countOutsideQuotes(firstLine, candidate);
    if (count > bestCount) {
      best = candidate;
      bestCount = count;
    }
  }
  return best;
}

function countOutsideQuotes(line: string, delimiter: string): number {
  let count = 0;
  let inQuotes = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"') inQuotes = !inQuotes;
    else if (!inQuotes && char === delimiter) count += 1;
  }
  return count;
}

/**
 * Zerlegt den Text in Zeichenketten -- bewusst ein echter Parser und kein `split(';')`:
 * ein Firmenname wie `"Meier; Söhne GmbH"` steht in Anfuehrungszeichen und enthaelt den Trenner.
 * Auch Zeilenumbrueche duerfen in Anfuehrungszeichen stehen (Arista tut das bei Adresszusaetzen).
 */
export function parseCsv(text: string, forcedDelimiter?: string): CsvTable {
  const delimiter = forcedDelimiter ?? detectDelimiter(text.split(/\r?\n/, 1)[0] ?? '');
  const records: { line: number; cells: string[] }[] = [];

  let cells: string[] = [];
  let field = '';
  let inQuotes = false;
  let line = 1;
  let recordStartLine = 1;

  const endField = () => { cells.push(field); field = ''; };
  const endRecord = () => {
    endField();
    records.push({ line: recordStartLine, cells });
    cells = [];
    recordStartLine = line + 1;
  };

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];

    if (inQuotes) {
      if (char === '"') {
        // "" innerhalb eines Feldes ist ein echtes Anfuehrungszeichen, kein Ende.
        if (text[index + 1] === '"') { field += '"'; index += 1; }
        else inQuotes = false;
      } else {
        if (char === '\n') line += 1;
        field += char;
      }
      continue;
    }

    if (char === '"' && field === '') { inQuotes = true; continue; }
    if (char === delimiter) { endField(); continue; }
    if (char === '\r') continue;
    if (char === '\n') { endRecord(); line += 1; continue; }
    field += char;
  }

  // Letzte Zeile ohne abschliessenden Umbruch nicht verlieren.
  if (field !== '' || cells.length > 0) endRecord();

  const [headerRecord, ...dataRecords] = records;
  const header = (headerRecord?.cells ?? []).map((cell) => cell.trim());

  const rows: CsvRow[] = [];
  const raggedLines: number[] = [];
  let blankRowsSkipped = 0;

  for (const record of dataRecords) {
    const trimmed = record.cells.map((cell) => cell.trim());
    if (trimmed.every((cell) => cell === '')) { blankRowsSkipped += 1; continue; }
    if (header.length > 0 && trimmed.length !== header.length) raggedLines.push(record.line);
    rows.push({ line: record.line, cells: trimmed });
  }

  return { header, rows, delimiter, blankRowsSkipped, raggedLines };
}

/** Zeile als benanntes Objekt -- fehlende Spalten werden zu '', nicht zu undefined. */
export function rowValues(header: readonly string[], row: CsvRow): Readonly<Record<string, string>> {
  const values: Record<string, string> = {};
  header.forEach((name, index) => { values[name] = row.cells[index] ?? ''; });
  return values;
}
